import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from './Navbar';
import RestaurantHeader from '../restaurant/RestaurantHeader';
import RestaurantInfo from '../restaurant/RestaurantInfo';
import { usePlaceData } from '../restaurant/UseRestaurantData';
import ReactMarkdown from 'react-markdown';
import Footer from './Footer';
import ReviewsSection from '../restaurant/ReveiwSection';
import ImageGallery from './ImageGallery';
import PlaceDetailHero from './PlaceDetailHero';
import WriteComment from './WriteComment';
import MapComponent from '../restaurant/MapComponent';
import useUserStore from "../store/userStore";
// import { Phone, MapPin } from 'lucide-react';
import useBreadcrumbStore from "./breadcrumbStore";
import { toast } from 'react-toastify';
import { useFavorite } from '../hooks/useFavorites';
import PlaceWeatherSection from './PlaceWeatherSection';

export interface ReviewImage {
  image_id: string;
  filename: string;
  file_url: string;
  description?: string | null;
}

export interface Review {
  review_id: string;
  place: string;
  place_name: string;
  user: string;
  user_name: string;
  full_name: string;
  rate: number;
  comment: string;
  date: string;
  images?: ReviewImage[];
}

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const PlaceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { restaurant, loading, error } = usePlaceData(id);
  const { user } = useUserStore();
  const userId = user?.userId;
  const placeCategory = restaurant?.categories?.[0]?.name || restaurant?.sub_category || restaurant?.category || "";
  const placeCity = restaurant?.cities?.[0]?.name || (typeof restaurant?.city === "string" ? restaurant.city : restaurant?.city?.name) || "";
  const isAttraction = /جاذبه|دیدنی|attraction/i.test(placeCategory);
  const canFetchPlaceWeather = Boolean(
    isAttraction && id && restaurant?.latitude != null && restaurant?.longitude != null,
  );
  const [isPlaceWeatherReady, setIsPlaceWeatherReady] = useState(false);
  const hasPlaceWeather = canFetchPlaceWeather && isPlaceWeatherReady;

  // وضعیت‌های مربوط به نظرات
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);

  // وضعیت‌های مربوط به بارگذاری مرحله‌ای نظرات
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalReviews, setTotalReviews] = useState<number | null>(null);
  const writeCommentRef = useRef<HTMLDivElement | null>(null);
  const generalInfoRef = useRef<HTMLDivElement | null>(null);
  const aboutRef = useRef<HTMLDivElement | null>(null);
  const weatherRef = useRef<HTMLDivElement | null>(null);
  const reviewsRef = useRef<HTMLDivElement | null>(null);
  const [activeMobileSection, setActiveMobileSection] = useState<'general' | 'about' | 'weather' | 'reviews'>('general');
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const {
    isFavorite,
    isLoading: isFavoriteLoading,
    toggleFavorite,
  } = useFavorite(userId, "place", id);
  const { setBreadcrumbData, clearBreadcrumbData } = useBreadcrumbStore();

  useEffect(() => {
    if (loading) {
      setBreadcrumbData(null, null, true);
    } else if (restaurant) {
      const categoryName = restaurant.categories?.[0]?.name || restaurant.sub_category || null;
      setBreadcrumbData(restaurant.name, categoryName, false);
    }

    return () => {
      clearBreadcrumbData();
    };
  }, [loading, restaurant, setBreadcrumbData, clearBreadcrumbData]);

  const scrollToWriteComment = () => {
    writeCommentRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToReviews = () => {
    reviewsRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const scrollToMobileSection = (
    section: 'general' | 'about' | 'weather' | 'reviews',
    sectionRef: React.RefObject<HTMLDivElement | null>
  ) => {
    setActiveMobileSection(section);
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // تابع دریافت نظرات با پشتیبانی از صفحه‌بندی و شافل
  const fetchReviews = async (page: number = 1, append: boolean = false) => {
    if (!id) return;

    setIsLoadingReviews(true);
    setReviewError(null);

    try {
      const params = new URLSearchParams({
        place_id: id,
        page: page.toString(),
        page_size: '10',
        shuffle: 'false'
      });

      const res = await fetch(`${BASE_URL}/api/reviews/?${params.toString()}`);

      if (!res.ok) throw new Error('Failed to fetch reviews');

      const data = await res.json();
      const rawResults = Array.isArray(data)
        ? data
        : Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data?.data?.results)
            ? data.data.results
            : Array.isArray(data?.data)
              ? data.data
              : data?.review_id ? [data] : [];
      const resultCount = Number(data?.count ?? data?.data?.count ?? rawResults.length) || rawResults.length;

      setReviews(prev => append ? [...prev, ...rawResults] : rawResults);
      setTotalPages(Math.max(1, Math.ceil(resultCount / 10)));
      setTotalReviews(resultCount);
      setCurrentPage(page);

    } catch (err) {
      console.error(err);
      setReviewError('خطا در دریافت نظرات کاربران.');
    } finally {
      setIsLoadingReviews(false);
    }
  };

  const handleToggleFavorite = async () => {
    if (!userId) {
      toast.error("لطفاً ابتدا وارد حساب کاربری خود شوید", {
        className: "toast",
        hideProgressBar: true,
      });
      return;
    }

    if (!id || isFavoriteLoading) return;

    try {
      const result = await toggleFavorite();

      toast.success(
        result.action === "added"
          ? "مکان به علاقه‌مندی‌ها اضافه شد"
          : "مکان از علاقه‌مندی‌ها حذف شد",
        {
          className: "toast",
          hideProgressBar: true,
        }
      );
    } catch (favoriteError) {
      console.error("Error toggling favorite:", favoriteError);

      toast.error("تغییر علاقه‌مندی با خطا مواجه شد", {
        className: "toast",
        hideProgressBar: true,
      });
    }
  };

  useEffect(() => {
    if (id) {
      fetchReviews(1, false);
    }
  }, [id]);

  useEffect(() => {
    setIsPlaceWeatherReady(false);
  }, [id, canFetchPlaceWeather]);

  useEffect(() => {
    let animationFrame: number | null = null;

    const updateActiveSection = () => {
      animationFrame = null;
      if (!window.matchMedia("(max-width: 743px)").matches) return;

      const sections: Array<{
        id: 'general' | 'about' | 'weather' | 'reviews';
        element: HTMLDivElement | null;
      }> = [
          { id: 'general', element: generalInfoRef.current },
          { id: 'about', element: aboutRef.current },
          ...(hasPlaceWeather ? [{ id: 'weather' as const, element: weatherRef.current }] : []),
          { id: 'reviews', element: reviewsRef.current },
        ];

      const activeSection = sections.reduce<'general' | 'about' | 'weather' | 'reviews'>(
        (current, section) => section.element && section.element.getBoundingClientRect().top <= 128
          ? section.id
          : current,
        'general',
      );

      setActiveMobileSection(activeSection);
    };

    const handleScroll = () => {
      if (animationFrame === null) {
        animationFrame = window.requestAnimationFrame(updateActiveSection);
      }
    };

    updateActiveSection();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
    };
  }, [hasPlaceWeather]);

  const handleLoadMoreReviews = (page: number) => {
    if (page <= totalPages && !isLoadingReviews) {
      fetchReviews(page, true);
    }
  };

  const handleLoadAllReviews = async () => {
    if (isLoadingReviews || currentPage >= totalPages) return;

    for (let page = currentPage + 1; page <= totalPages; page += 1) {
      await fetchReviews(page, true);
    }
  };

  const handleReviewSubmitted = useCallback((newReview: Review) => {

    setReviews(prevReviews => {
      // جلوگیری از تکرار کامنت
      const exists = prevReviews.some(r => r.review_id === newReview.review_id);
      if (exists) {
        console.log("⚠️ کامنت تکراری است");
        return prevReviews;
      }

      // اضافه کردن کامنت جدید به ابتدای لیست
      const updatedReviews = [newReview, ...prevReviews];
      return updatedReviews;
    });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white py-8 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-lootka-green"></div>
      </div>
    );
  }

  if (error || !restaurant) {
    return (
      <div className="min-h-screen bg-white py-8">
        <div className="site-container">
          <h1 className="text-3xl font-myYekanFaNumExtraBold text-gray-900">مکان مورد نظر پیدا نشد.</h1>
          <p className="text-gray-600 mt-2">{error}</p>
          <Link
            to="/restaurant"
            className="font-myYekanRegular hover:underline mt-4 inline-block"
          >
            بازگشت
          </Link>
        </div>
      </div>
    );
  }

  const reviewCount = totalReviews ?? (typeof restaurant.reviews === "number" ? restaurant.reviews : 0);

  return (
    <div className="min-h-screen bg-white pb-1 tablet:py-1">
      <Navbar />

      <div className="contents tablet:hidden">
        <PlaceDetailHero
          placeId={id ?? ""}
          name={restaurant.name}
          rate={restaurant.rate}
          reviewCount={reviewCount}
          onReviewsClick={scrollToReviews}
          category={placeCategory}
          openingHours={restaurant.opening_hours}
          phone={restaurant.phone}
          latitude={restaurant.latitude}
          longitude={restaurant.longitude}
          images={restaurant.images || []}
          baseUrl={BASE_URL}
          onFavoriteClick={handleToggleFavorite}
          isFavorite={isFavorite}
          isFavoriteLoading={isFavoriteLoading}
          mobileNavigation={
            <nav
              className={`-mx-4 mt-2 grid border-t border-gray-200 bg-white px-1 font-myYekanDemibold ${hasPlaceWeather ? "grid-cols-4" : "grid-cols-3"}`}
              aria-label="بخش‌های جزئیات مکان"
            >
              <button
                type="button"
                onClick={() => scrollToMobileSection('general', generalInfoRef)}
                className={`flex h-8 items-center justify-center whitespace-nowrap px-0.5 text-[11px] transition-colors ${activeMobileSection === 'general'
                  ? 'text-[#038545]'
                  : 'text-[#222222] hover:text-black'
                  }`}
              >
                اطلاعات عمومی
              </button>
              <button
                type="button"
                onClick={() => scrollToMobileSection('about', aboutRef)}
                className={`flex h-8 items-center justify-center whitespace-nowrap px-0.5 text-[11px] transition-colors ${activeMobileSection === 'about'
                  ? 'text-[#038545]'
                  : 'text-[#222222] hover:text-black'
                  }`}
              >
                درباره مکان
              </button>
              {hasPlaceWeather && (
                <button
                  type="button"
                  onClick={() => scrollToMobileSection('weather', weatherRef)}
                  className={`flex h-8 items-center justify-center whitespace-nowrap px-0.5 text-[11px] transition-colors ${activeMobileSection === 'weather'
                    ? 'text-[#038545]'
                    : 'text-[#222222] hover:text-black'
                    }`}
                >
                  آب‌وهوا
                </button>
              )}
              <button
                type="button"
                onClick={() => scrollToMobileSection('reviews', reviewsRef)}
                className={`flex h-8 items-center justify-center whitespace-nowrap px-0.5 text-[11px] transition-colors ${activeMobileSection === 'reviews'
                  ? 'text-[#038545]'
                  : 'text-[#222222] hover:text-black'
                  }`}
              >
                نظرات
              </button>
            </nav>
          }
        />
      </div>

      <div className="site-container mt-0 w-full flex flex-col gap-6 pb-24 pt-0 tablet:mt-20">
        <div className="hidden tablet:block w-full">
          <div className="relative tablet:[&>div]:mb-0 desktop:[&>div]:mb-3">
            <RestaurantHeader
              restaurant={{
                ...restaurant,
                reviewCount,
              }}
              cityName={placeCity}
              onWriteCommentClick={scrollToWriteComment}
              onReviewsClick={scrollToReviews}
              onFavoriteClick={handleToggleFavorite}
              isFavorite={isFavorite}
              isFavoriteLoading={isFavoriteLoading}
            />
          </div>
          <div className="relative">
            <ImageGallery
              images={restaurant.images || []}
              restaurantName={restaurant.name}
              baseUrl={BASE_URL}
              onFavoriteClick={handleToggleFavorite}
              isFavorite={isFavorite}
              isFavoriteLoading={isFavoriteLoading}
              onWriteCommentClick={scrollToWriteComment}
            />
          </div>
        </div>

        {/* Main Content Layout */}
        <div className='flex flex-col lg:flex-row gap-6'>
          {/* Left Column */}
          <div className="w-full lg:w-2/3 flex flex-col">
            {/* Place Info (Mobile) */}
            <div ref={generalInfoRef} className="w-full scroll-mt-32 tablet:hidden">
              <RestaurantInfo restaurant={restaurant} addressFirst placeId={id} placeName={restaurant.name} />
            </div>

            {/* Description Section */}
            <div ref={aboutRef} className="mt-3 w-full scroll-mt-32 tablet:mt-0">
              <section className="rounded-xl px-0 py-4 tablet:px-0 tablet:py-6">
                <h2 className="mb-4 hidden text-[1.09rem] font-myYekanFaNumExtraBold leading-[1.5] text-[#222222] tablet:block">
                  درباره این مکان
                </h2>
                <div>
                  <div
                    className={`max-w-full space-y-3 whitespace-pre-line break-words text-justify text-[0.77rem] font-myYekanRegular font-normal leading-[2] text-black tablet:text-black [overflow-wrap:anywhere] transition-all duration-300 ease-in-out ${isDescriptionExpanded ? '' : 'line-clamp-4 lg:line-clamp-none'
                      }`}
                  >
                    <ReactMarkdown>{restaurant.description}</ReactMarkdown>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsDescriptionExpanded((current) => !current)}
                    className="mt-2 py-1 text-xs font-myYekanDemibold text-[#068646] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#038545] lg:hidden"
                  >
                    {isDescriptionExpanded ? "بستن" : "مشاهده بیشتر..."}
                  </button>
                </div>
              </section>
            </div>

            {canFetchPlaceWeather && id && (
              <div ref={weatherRef} className={hasPlaceWeather ? "mt-3 w-full scroll-mt-32 tablet:mt-6" : "contents"}>
                <PlaceWeatherSection
                  placeId={id}
                  placeName={restaurant.name}
                  onReadyChange={setIsPlaceWeatherReady}
                />
              </div>
            )}

            {/* Map (Mobile) */}
            <div className="mt-3 w-full tablet:hidden">
              {restaurant.latitude && restaurant.longitude && (
                <MapComponent
                  placeId={id}
                  latitude={restaurant.latitude}
                  longitude={restaurant.longitude}
                  name={restaurant.name}
                  address={restaurant.address}
                />
              )}
            </div>

            {/* Info Section (Tablet) */}
            <div className="hidden w-full mt-6 tablet:block lg:hidden">
              <RestaurantInfo restaurant={restaurant} placeId={id} placeName={restaurant.name} />
              <div className='mt-6'>
                {restaurant.latitude && restaurant.longitude && (
                  <MapComponent
                    placeId={id}
                    latitude={restaurant.latitude}
                    longitude={restaurant.longitude}
                  name={restaurant.name}
                  address={restaurant.address}
                />
                )}
              </div>
            </div>

            {/* Reviews Section */}
            <div className="w-full mt-6">
              <section ref={writeCommentRef} className="tablet:rounded-xl tablet:px-0 tablet:py-6">
                <h2 className="hidden text-[1.09rem] font-myYekanFaNumExtraBold leading-[1.5] text-[#222222] tablet:block">
                  به {restaurant.name} امتیاز دهید:
                </h2>
                {id && (
                  <WriteComment
                    placeId={id}
                    userId={userId}
                    onReviewSubmitted={handleReviewSubmitted}
                  />
                )}
              </section>

              <div ref={reviewsRef} className="scroll-mt-32">
                <ReviewsSection
                  placeId={id ?? ""}
                  placeName={restaurant.name}
                  reviewSummary={restaurant.review_summary}
                  reviews={reviews}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalReviews={reviewCount}
                  onPageChange={handleLoadMoreReviews}
                  onLoadAllReviews={handleLoadAllReviews}
                  isLoading={isLoadingReviews}
                />
              </div>
            </div>
          </div>

          {/* Right Column: Info (Desktop Only) */}
          <div className="hidden lg:block w-1/3 mt-2">
            <RestaurantInfo restaurant={restaurant} placeId={id} placeName={restaurant.name} />
            <div className='mt-6'>
              {restaurant.latitude && restaurant.longitude && (
                <MapComponent
                  placeId={id}
                  latitude={restaurant.latitude}
                  longitude={restaurant.longitude}
                  name={restaurant.name}
                  address={restaurant.address}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sticky Action Bar temporarily hidden
      <div className="font-myYekanDemibold md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-2 z-50 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
        <div className="flex gap-2">
          <a
            href={`geo:${restaurant.latitude},${restaurant.longitude}?q=${restaurant.latitude},${restaurant.longitude}(${encodeURIComponent(restaurant.name)})`}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-[#038545] bg-white py-3 text-[0.77rem] font-myYekanDemibold text-[#068646] transition-colors hover:bg-[#f3fefa]"
          >
            <MapPin size={20} />
            <span>مسیریابی</span>
          </a>
          {restaurant.phone && (
            <a
              href={`tel:${restaurant.phone}`}
              className={`flex items-center justify-center gap-2 rounded-xl border border-[#038545] bg-[#038545] py-3 text-[0.77rem] font-myYekanDemibold text-white transition-colors hover:bg-[#068646] ${restaurant.phone
                  ? 'flex-1'
                  : 'w-full'
                }`}
              style={{ color: 'white' }}
            >
              <Phone size={20} />
              <span>تماس</span>
            </a>
          )}
        </div>
      </div>
      */}

      <Footer />
    </div>
  );
};

export default PlaceDetail;
